import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import ArrowRightIcon from '@mui/icons-material/ArrowForward';
import ArrowLeftIcon from '@mui/icons-material/ArrowBack';
import { AutoCompleteField, Dropdown } from '../../components/widgets';
import { CustomMetricsContext } from '../context';
import { TRANSLATION_PREFIX, getDefaultResult } from '../consts';
import { filterByLabelOnly } from './selectFilter';
import css from '../page.css';

const FILTER_METRIC_PLACEHOLDER = `${TRANSLATION_PREFIX}.PLACEHOLDERS.FILTER_METRIC`;
const BLANK_OPERATOR_OPTION_NAME = 'default';

const resultErrorsShape = PropTypes.shape({
    metric: PropTypes.bool,
    metricCollision: PropTypes.bool,
    opValue: PropTypes.bool,
});

const getValueDisplay = value => {
    if (value === null || value === undefined) {
        return '';
    }

    return typeof value === 'object' ? value.display_name : `${value}`;
};

function ResultRow({ result, onChange, errors = null, showErrors = false }) {
    const { visibleMetrics, allResultValues, resultOperators } = useContext(CustomMetricsContext);
    // Remount the value autocomplete after a selection so it re-reads the (object) value;
    // typing must not remount (it would lose focus), hence a counter and not the value itself.
    const [valueSelectionCount, setValueSelectionCount] = useState(0);

    // AutoCompleteField captures its onInputChange prop once per mount, so the typed-value
    // handler must read the latest result/onChange through refs instead of its closure.
    const resultRef = useRef(result);
    resultRef.current = result;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const metricOptions = visibleMetrics.map(metric => ({ value: metric.name, label: metric.display_name }));
    const valueOptions = allResultValues.map(value => ({ value: value.name, label: value.display_name }));
    const operatorItems = resultOperators.map(operator => ({
        ...operator,
        name: operator.name || BLANK_OPERATOR_OPTION_NAME,
        display_name: operator.operator,
    }));
    const selectedOperator = operatorItems.find(({ name }) => name === (result.operator.name || BLANK_OPERATOR_OPTION_NAME));

    const onMetricSelected = option => {
        const metric = visibleMetrics.find(({ name }) => name === option.value) || getDefaultResult().metric;
        onChange({ ...result, metric });
    };

    const onOperatorSelected = operatorItem => {
        const operator = resultOperators.find(({ name }) => name === operatorItem.name) || resultOperators[0];
        onChange({ ...result, operator });
    };

    const onValueSelected = option => {
        const value = allResultValues.find(({ name }) => name === option.value);
        setValueSelectionCount(count => count + 1);
        onChange({ ...result, value, isRefTable: !!value?.isRefTable });
    };

    const onValueTyped = text => {
        onChangeRef.current({ ...resultRef.current, value: text, isRefTable: false });
    };

    const onToggleOpValue = () => {
        if (result.showOpValue) {
            const defaultResult = getDefaultResult();
            onChange({
                ...result,
                showOpValue: false,
                _previous: { operator: result.operator, value: result.value, isRefTable: result.isRefTable },
                operator: defaultResult.operator,
                value: defaultResult.value,
                isRefTable: false,
            });
        } else {
            onChange({
                ...result,
                showOpValue: true,
                ...(result._previous || {}),
            });
        }
    };

    const refTableLink = result.isRefTable && typeof result.value === 'object' && result.value !== null && (
        <a
            className={css.refTableLink}
            href={`#/reference/${result.value.slug_name || result.value.name}`}
            target="_blank"
            rel="noreferrer"
        >
            <span className="icon-document-table" />
        </a>
    );

    // Selected values must be option objects: displaying them through a controlled input
    // string makes them vanish whenever react-select hides its input (menu toggle, blur).
    const isObjectValue = typeof result.value === 'object' && result.value !== null;
    const selectedMetricOption = result.metric?.name
        ? { value: result.metric.name, label: result.metric.display_name }
        : null;

    return (
        <div>
            <div className={css.resultRow}>
                <div className={css.resultField}>
                    <AutoCompleteField
                        key={`metric-${result.metric?.name || ''}`}
                        isMulti={false}
                        searchable
                        options={metricOptions}
                        value={selectedMetricOption}
                        placeholder={FILTER_METRIC_PLACEHOLDER}
                        onChange={onMetricSelected}
                        selectOptions={{ filterOption: filterByLabelOnly }}
                        error={
                            showErrors && errors?.metric
                                ? 'STATIC.REQUIRED_FIELD'
                                : showErrors && errors?.metricCollision
                                ? 'STATIC.FIELD_COLLISION'
                                : ''
                        }
                    />
                </div>
                {result.showOpValue && (
                    <div className={css.resultOperator}>
                        <Dropdown
                            items={operatorItems}
                            selected={selectedOperator}
                            onSelection={onOperatorSelected}
                            disabled={!result.metric?.name}
                            containerStyle={{ width: '100%' }}
                            wrapperStyle={{ width: '100%' }}
                        />
                    </div>
                )}
                {result.showOpValue && (
                    <div className={css.resultField}>
                        <AutoCompleteField
                            key={`value-${valueSelectionCount}`}
                            isMulti={false}
                            searchable
                            controlled={!isObjectValue}
                            options={valueOptions}
                            value={
                                isObjectValue
                                    ? { value: result.value.name, label: result.value.display_name }
                                    : getValueDisplay(result.value)
                            }
                            placeholder={FILTER_METRIC_PLACEHOLDER}
                            onChange={onValueSelected}
                            onInputChange={onValueTyped}
                            debounceTime={0}
                            disabled={!result.metric?.name}
                            selectOptions={{ filterOption: filterByLabelOnly }}
                        />
                    </div>
                )}
                {result.showOpValue && refTableLink}
                <button
                    type="button"
                    className={css.opValueToggle}
                    title={result.showOpValue ? 'Remove operator and value' : 'Add operator and value'}
                    onClick={onToggleOpValue}
                >
                    {result.showOpValue ? <ArrowLeftIcon /> : <ArrowRightIcon />}
                </button>
            </div>
            {showErrors && errors?.opValue && (
                <div className={css.errorText}>
                    <Translate id={`${TRANSLATION_PREFIX}.BOTH_FIELDS_REQUIRED`} />
                </div>
            )}
        </div>
    );
}

ResultRow.propTypes = {
    result: PropTypes.shape({
        metric: PropTypes.objectOf(PropTypes.any),
        operator: PropTypes.objectOf(PropTypes.any),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
        isRefTable: PropTypes.bool,
        showOpValue: PropTypes.bool,
        _previous: PropTypes.objectOf(PropTypes.any),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    errors: resultErrorsShape,
    showErrors: PropTypes.bool,
};

export default ResultRow;
